import { USER_GROUP } from '@/helpers/user';

export enum MissionStatusGroups {
  Daily = 'xs_mission',
  Casual = 'casual',
  VIP = 'vip',
}

export enum MissionMode {
  PastFailed = 'PAST_FAILED',
  PastCompleted = 'PAST_COMPLETED',
  TodayFailed = 'TODAY_FAILED',
  TodayActive = 'TODAY_ACTIVE',
  TodayCompleted = 'TODAY_COMPLETED',
  Future = 'FUTURE',
}

interface DetermineMissionModeParams {
  isAuthenticated: boolean;
  isComplete: boolean;
  isToday: boolean;
  isFuture: boolean;
  missionStarted: boolean;
}

export function determineMissionMode({
  isAuthenticated,
  isComplete,
  isToday,
  isFuture,
  missionStarted,
}: DetermineMissionModeParams): MissionMode {
  if (!isAuthenticated) {
    return MissionMode.Future;
  }

  if (isComplete) {
    if (isToday) {
      return MissionMode.TodayCompleted;
    }
    return MissionMode.PastCompleted;
  }

  if (isFuture) {
    return MissionMode.Future;
  }

  if (isToday) {
    if (missionStarted) {
      return MissionMode.TodayActive;
    }
    return MissionMode.TodayFailed;
  }

  return MissionMode.PastFailed;
}

export type MissionType = 'deposit' | 'playGames';
export type MissionPrizeType = 'cash' | 'fs' | 'lootbox' | 'tg/wa';

const getDepBonusUrl = (bonusCode: string) =>
  `?modal=deposit&bonus_code=${bonusCode}`;
const promotionUrl = '/promotions?tab=active-bonuses';
export interface QuestMission {
  date: string;
  title: string;
  type: MissionType;
  mission?: string;
  vipMission?: string;
  missionPerGroup?: Record<string, string>;
  terms: string;
  prizeType: MissionPrizeType;
  completeImg: string;
  prizeUrl?: string;
  missionUrl?: string;
  missionUrlPerGroup?: Record<string, string>;
  missionExternalUrls?: string[];
  isSpecialMission?: boolean;
}

export interface QuestMissionWithMode extends QuestMission {
  mode: MissionMode;
}

export const questMissions: QuestMission[] = [
  {
    date: '2025-12-01',
    type: 'playGames',
    prizeType: 'fs',
    missionUrl: '/',
    prizeUrl: promotionUrl,
    completeImg: '/quest-xmas/mission/character-0.webp',
    title: 'missions.0.title',
    mission: 'missions.0.mission',
    vipMission: 'missions.0.vipMission',
    terms: 'missions.0.terms',
  },
  {
    date: '2025-12-02',
    type: 'deposit',
    prizeType: 'cash',
    missionUrl: '?modal=deposit',
    completeImg: '/quest-xmas/mission/character-1.webp',
    title: 'missions.1.title',
    mission: 'missions.1.mission',
    vipMission: 'missions.1.vipMission',
    terms: 'missions.1.terms',
  },
  {
    date: '2025-12-03',
    type: 'playGames',
    prizeType: 'fs',
    missionUrl: '/',
    prizeUrl: promotionUrl,
    completeImg: '/quest-xmas/mission/character-2.webp',
    title: 'missions.2.title',
    mission: 'missions.2.mission',
    vipMission: 'missions.2.vipMission',
    terms: 'missions.2.terms',
  },
  {
    date: '2025-12-04',
    type: 'deposit',
    prizeType: 'fs',
    prizeUrl: promotionUrl,
    missionUrlPerGroup: {
      [USER_GROUP.Champions]: getDepBonusUrl('FORTUNEUP'),
      [USER_GROUP.BONUS_HUNTERS]: getDepBonusUrl('FORTUNE50'),
      [USER_GROUP.GOLD]: getDepBonusUrl('VIPBOX'),
      [USER_GROUP.PLATINUM]: getDepBonusUrl('VIPBOX'),
      [USER_GROUP.DIAMOND]: getDepBonusUrl('VIPBOX'),
      default: getDepBonusUrl('FORTUNELVL'),
    },
    missionPerGroup: {
      [USER_GROUP.Champions]: 'missions.3.champMission',
      [USER_GROUP.BONUS_HUNTERS]: 'missions.3.bhMission',
      [USER_GROUP.GOLD]: 'missions.3.vipMission',
      [USER_GROUP.PLATINUM]: 'missions.3.vipMission',
      [USER_GROUP.DIAMOND]: 'missions.3.vipMission',
      default: 'missions.3.mission',
    },
    completeImg: '/quest-xmas/mission/character-3.webp',
    title: 'missions.3.title',
    terms: 'missions.3.terms',
  },
  {
    date: '2025-12-05',
    type: 'playGames',
    prizeType: 'fs',
    missionUrl: '/games/merry_wins#gamesSection',
    completeImg: '/quest-xmas/mission/character-4.webp',
    title: 'missions.4.title',
    mission: 'missions.4.mission',
    vipMission: 'missions.4.vipMission',
    terms: 'missions.4.terms',
  },
  {
    date: '2025-12-06',
    type: 'deposit',
    prizeType: 'cash',
    missionUrl: '?modal=deposit',
    completeImg: '/quest-xmas/mission/character-5.webp',
    title: 'missions.5.title',
    mission: 'missions.5.mission',
    vipMission: 'missions.5.vipMission',
    terms: 'missions.5.terms',
  },
  {
    date: '2025-12-07',
    type: 'playGames',
    prizeType: 'lootbox',
    missionUrl: '/',
    completeImg: '/quest-xmas/mission/character-6.webp',
    title: 'missions.6.title',
    mission: 'missions.6.mission',
    vipMission: 'missions.6.vipMission',
    terms: 'missions.6.terms',
  },
  {
    date: '2025-12-08',
    type: 'deposit',
    prizeType: 'fs',
    prizeUrl: promotionUrl,
    missionUrlPerGroup: {
      [USER_GROUP.Champions]: getDepBonusUrl('TURBOUP'),
      [USER_GROUP.BONUS_HUNTERS]: getDepBonusUrl('TURBO55'),
      [USER_GROUP.GOLD]: getDepBonusUrl('VIPBOX'),
      [USER_GROUP.PLATINUM]: getDepBonusUrl('VIPBOX'),
      [USER_GROUP.DIAMOND]: getDepBonusUrl('VIPBOX'),
      default: getDepBonusUrl('TURBOLVL'),
    },
    missionPerGroup: {
      [USER_GROUP.Champions]: 'missions.7.champMission',
      [USER_GROUP.BONUS_HUNTERS]: 'missions.7.bhMission',
      [USER_GROUP.GOLD]: 'missions.7.vipMission',
      [USER_GROUP.PLATINUM]: 'missions.7.vipMission',
      [USER_GROUP.DIAMOND]: 'missions.7.vipMission',
      default: 'missions.7.mission',
    },
    completeImg: '/quest-xmas/mission/character-7.webp',
    title: 'missions.7.title',
    terms: 'missions.7.terms',
  },
  {
    date: '2025-12-09',
    type: 'playGames',
    prizeType: 'fs',
    missionUrl: '/games/new#gamesSection',
    prizeUrl: promotionUrl,
    completeImg: '/quest-xmas/mission/character-8.webp',
    title: 'missions.8.title',
    mission: 'missions.8.mission',
    vipMission: 'missions.8.vipMission',
    terms: 'missions.8.terms',
  },
  {
    date: '2025-12-10',
    type: 'playGames',
    prizeType: 'fs',
    missionUrl: '/',
    completeImg: '/quest-xmas/mission/character-9.webp',
    title: 'missions.9.title',
    mission: 'missions.9.mission',
    vipMission: 'missions.9.vipMission',
    terms: 'missions.9.terms',
  },
  {
    date: '2025-12-11',
    type: 'playGames',
    prizeType: 'lootbox',
    missionUrl: '/',
    completeImg: '/quest-xmas/mission/character-10.webp',
    title: 'missions.10.title',
    mission: 'missions.10.mission',
    vipMission: 'missions.10.vipMission',
    terms: 'missions.10.terms',
  },
  {
    date: '2025-12-12',
    type: 'deposit',
    prizeType: 'cash',
    missionUrl: '?modal=deposit',
    completeImg: '/quest-xmas/mission/character-11.webp',
    title: 'missions.11.title',
    mission: 'missions.11.mission',
    vipMission: 'missions.11.vipMission',
    terms: 'missions.11.terms',
  },
  {
    date: '2025-12-13',
    type: 'playGames',
    prizeType: 'fs',
    missionUrl: '/',
    prizeUrl: promotionUrl,
    completeImg: '/quest-xmas/mission/character-12.webp',
    title: 'missions.12.title',
    mission: 'missions.12.mission',
    vipMission: 'missions.12.vipMission',
    terms: 'missions.12.terms',
  },
  {
    date: '2025-12-14',
    type: 'playGames',
    prizeType: 'fs',
    missionUrl: '/games/merry_wins#gamesSection',
    prizeUrl: promotionUrl,
    completeImg: '/quest-xmas/mission/character-13.webp',
    title: 'missions.13.title',
    mission: 'missions.13.mission',
    vipMission: 'missions.13.vipMission',
    terms: 'missions.13.terms',
  },
  {
    date: '2025-12-15',
    type: 'playGames',
    prizeType: 'fs',
    missionUrl: '/',
    prizeUrl: promotionUrl,
    completeImg: '/quest-xmas/mission/character-14.webp',
    title: 'missions.14.title',
    mission: 'missions.14.mission',
    vipMission: 'missions.14.vipMission',
    terms: 'missions.14.terms',
  },
  {
    date: '2025-12-16',
    type: 'deposit',
    prizeType: 'cash',
    missionUrl: '?modal=deposit',
    completeImg: '/quest-xmas/mission/character-15.webp',
    title: 'missions.15.title',
    mission: 'missions.15.mission',
    vipMission: 'missions.15.vipMission',
    terms: 'missions.15.terms',
  },
  {
    date: '2025-12-17',
    type: 'deposit',
    prizeType: 'fs',
    prizeUrl: promotionUrl,
    missionUrlPerGroup: {
      [USER_GROUP.Champions]: getDepBonusUrl('LUCKYUP'),
      [USER_GROUP.BONUS_HUNTERS]: getDepBonusUrl('LUCKY35'),
      [USER_GROUP.GOLD]: getDepBonusUrl('VIPBOX'),
      [USER_GROUP.PLATINUM]: getDepBonusUrl('VIPBOX'),
      [USER_GROUP.DIAMOND]: getDepBonusUrl('VIPBOX'),
      default: getDepBonusUrl('LUCKYLVL'),
    },
    missionPerGroup: {
      [USER_GROUP.Champions]: 'missions.16.champMission',
      [USER_GROUP.BONUS_HUNTERS]: 'missions.16.bhMission',
      [USER_GROUP.GOLD]: 'missions.16.vipMission',
      [USER_GROUP.PLATINUM]: 'missions.16.vipMission',
      [USER_GROUP.DIAMOND]: 'missions.16.vipMission',
      default: 'missions.16.mission',
    },
    completeImg: '/quest-xmas/mission/character-16.webp',
    title: 'missions.16.title',
    terms: 'missions.16.terms',
  },
  {
    date: '2025-12-18',
    type: 'playGames',
    prizeType: 'fs',
    missionUrl: '/games/merry_wins#gamesSection',
    prizeUrl: promotionUrl,
    completeImg: '/quest-xmas/mission/character-17.webp',
    title: 'missions.17.title',
    mission: 'missions.17.mission',
    vipMission: 'missions.17.vipMission',
    terms: 'missions.17.terms',
  },
  {
    date: '2025-12-19',
    type: 'playGames',
    prizeType: 'fs',
    missionUrl: '/',
    prizeUrl: promotionUrl,
    completeImg: '/quest-xmas/mission/character-18.webp',
    title: 'missions.18.title',
    mission: 'missions.18.mission',
    vipMission: 'missions.18.vipMission',
    terms: 'missions.18.terms',
  },
  {
    date: '2025-12-20',
    type: 'playGames',
    prizeType: 'lootbox',
    missionUrl: '/',
    completeImg: '/quest-xmas/mission/character-19.webp',
    title: 'missions.19.title',
    mission: 'missions.19.mission',
    vipMission: 'missions.19.vipMission',
    terms: 'missions.19.terms',
  },
  {
    date: '2025-12-21',
    type: 'deposit',
    prizeType: 'cash',
    missionUrl: '?modal=deposit',
    completeImg: '/quest-xmas/mission/character-20.webp',
    title: 'missions.20.title',
    mission: 'missions.20.mission',
    vipMission: 'missions.20.vipMission',
    terms: 'missions.20.terms',
  },
  {
    date: '2025-12-22',
    type: 'playGames',
    prizeType: 'fs',
    missionUrl: '/',
    prizeUrl: promotionUrl,
    completeImg: '/quest-xmas/mission/character-21.webp',
    title: 'missions.21.title',
    mission: 'missions.21.mission',
    vipMission: 'missions.21.vipMission',
    terms: 'missions.21.terms',
  },
  {
    date: '2025-12-23',
    type: 'playGames',
    prizeType: 'fs',
    missionUrl: '/',
    prizeUrl: promotionUrl,
    completeImg: '/quest-xmas/mission/character-22.webp',
    title: 'missions.22.title',
    mission: 'missions.22.mission',
    vipMission: 'missions.22.vipMission',
    terms: 'missions.22.terms',
  },
  {
    date: '2025-12-24',
    type: 'deposit',
    prizeType: 'cash',
    isSpecialMission: true,
    completeImg: '/quest-xmas/mission/character-23.webp',
    title: 'missions.23.title',
    mission: 'missions.23.mission',
    vipMission: 'missions.23.vipMission',
    terms: 'missions.23.terms',
  },
];
